/**
 * Notes registry.
 *
 * The single source of truth for what exists at /notes/. Routing reads
 * from this list. Adding a new essay: write the markdown, add a row.
 *
 * Per the spec (§05): one essay published at launch (`the-quiet-morning`),
 * the other four scaffolded as `scheduled` placeholders. Direct URL access
 * to a `scheduled` slug renders a single-line letter — never a 404.
 */

export type PostStatus = 'published' | 'scheduled';

export interface PostMeta {
	slug: string;
	title: string;
	description: string;
	status: PostStatus;
	/** ISO 8601. Set on published; left empty on scheduled. */
	publishedAt?: string;
	readingTimeMinutes: number;
	/** Short eyebrow line for the index card. */
	eyebrow: string;
}

export const POSTS: ReadonlyArray<PostMeta> = [
	{
		slug: 'the-quiet-morning',
		title: 'The morning the work is already done.',
		description:
			"What it feels like when the inbox is sorted, the appeals are drafted, and the first sentence you read is 'I've handled four overnight. One needs you.'",
		status: 'published',
		publishedAt: '2026-05-05',
		readingTimeMinutes: 9,
		eyebrow: 'A long-form letter on the Ekavi morning'
	},
	{
		slug: 'hipaa-2026-readiness',
		title: 'The 2026 HIPAA Security Rule, plainly.',
		description:
			'What the May 2026 final rule actually means for an independent practice — written for a practice manager who does not have a compliance officer down the hall.',
		status: 'scheduled',
		readingTimeMinutes: 12,
		eyebrow: 'On compliance · scheduled'
	},
	{
		slug: 'the-7-day-payer-clock',
		title: 'Payers got faster. The PA queue did not.',
		description:
			'Starting January 1, 2026, payers must decide standard prior auths in seven days and urgent in seventy-two hours. The constraint just inverted.',
		status: 'scheduled',
		readingTimeMinutes: 8,
		eyebrow: 'On prior auth · scheduled'
	},
	{
		slug: 'ai-vendor-baa-red-flags',
		title: "Five lines to look for in any AI vendor's BAA.",
		description:
			"A buyer's checklist for the BAA every AI vendor will ask you to sign in 2026. The five clauses that separate vendors who think about HIPAA from those who don't.",
		status: 'scheduled',
		readingTimeMinutes: 10,
		eyebrow: 'On vendor diligence · scheduled'
	},
	{
		slug: 'the-audit-log-you-already-need',
		title: 'The audit log you already need.',
		description:
			'Forensic logging used to be a regulatory checkbox. In 2026 it is a daily operating requirement. What to look for, what to insist on, what to walk away from.',
		status: 'scheduled',
		readingTimeMinutes: 9,
		eyebrow: 'On audit logs · scheduled'
	}
];

export function postBySlug(slug: string): PostMeta | undefined {
	return POSTS.find((p) => p.slug === slug);
}

export function publishedPosts(): PostMeta[] {
	return POSTS.filter((p) => p.status === 'published');
}

export function scheduledPosts(): PostMeta[] {
	return POSTS.filter((p) => p.status === 'scheduled');
}
